
import React from 'react';

import { Avatar } from '../../../base/avatar';
import { getPictureInPictureModeConfig } from '../../../base/config/functions.any';
import { truncateText } from '../../../base/environment/utils';
import { translate } from '../../../base/i18n';
import { connect } from '../../../base/redux';
import { getBrandingColorPalette } from '../../../base/styles';
import { LobbySection } from '../../../lobby';
import { showNotification } from '../../../notifications';
import { isToolboxVisible } from '../../../toolbox/functions.web';
import { setKnockingParticipantApproval } from '../../actions';
import { admitAllLobbyParticipants } from '../../actions.any';
import AbstractKnockingParticipantList, {
    type Props as AbstractProps,
    mapStateToProps as abstractMapStateToProps
} from '../AbstractKnockingParticipantList';


declare var interfaceConfig: Object;
declare var config: object;

type Props = AbstractProps & {

    /**
     * True if the toolbox is visible, so we need to adjust the position.
     */
    _toolboxVisible: boolean,
};

/**
 * Component to render a list for the actively knocking participants.
 */
class KnockingParticipantList extends AbstractKnockingParticipantList<Props> {
    /**
     * Admit all knocking participants.
     */
    _onAdmitAllParticipant = () => {
        const { _knockingParticipants, _participantsinConference, participantAllowedinMeeting, dispatch } = this.props;

        const availableSlots = participantAllowedinMeeting - _participantsinConference.length;
        const participantsAllowedToJoin = _knockingParticipants.slice(0, availableSlots);

        dispatch(admitAllLobbyParticipants(participantsAllowedToJoin));

        if (_knockingParticipants > availableSlots) {

            dispatch(showNotification({
                descriptionArguments: { number: participantAllowedinMeeting },
                descriptionKey: 'notify.participantLimitReachedDes',
                titleKey: 'notify.participantLimitReached',
                customActionNameKey: 'notify.upgradeNow',
                customActionHandler: () => window.open(`${config?.URL_PREFIX}updateSubscription`, '_new')
            }));

            setTimeout(() => {
                participantsAllowedToJoin.map(p => dispatch(setKnockingParticipantApproval(p.id, false)));
            }, 8000);
        }

    };

    /**
     * Implements {@code PureComponent#render}.
     *
     * @inheritdoc
     */
    render() {
        const { _knockingParticipants, _toolboxVisible, _visible, _isPictureInPictureVisible, t } = this.props;
        const smallScreen = window.innerWidth < 500 || _isPictureInPictureVisible;
        const knockingParticipantStyle = {
            backgroundColor: getBrandingColorPalette(interfaceConfig)?.primary
        };
        const rejectButtonStyle = {
            backgroundColor: getBrandingColorPalette(interfaceConfig)?.accent2
        };

        if (!_visible) {
            return null;
        }

        return (
            <div
                className = { _toolboxVisible ? 'toolbox-visible' : '' }
                id = 'knocking-participant-list'
                style = { knockingParticipantStyle }>
                <span className = 'title'>
                    {t('lobby.knockingParticipantList')}
                    <div className = 'knocking-participant-list-actions'>
                        {!_isPictureInPictureVisible && <LobbySection hide = { true } />}
                        {_knockingParticipants.length > 1 && (
                            <button onClick = { this._onAdmitAllParticipant }>
                                {t('lobby.admitAll')}
                            </button>
                        )}
                    </div>
                </span>
                <ul>
                    {_knockingParticipants.map(p => (
                        <li key = { p.id }>
                            <Avatar
                                displayName = { p.name }
                                size = { 48 }
                                testId = 'knockingParticipant.avatar'
                                url = { p.loadableAvatarUrl } />
                            <div className = 'details'>
                                <span data-testid = 'knockingParticipant.name'>
                                    {smallScreen ? truncateText(p.name, 15) : p.name}
                                </span>
                                {p.email && (
                                    <span data-testid = 'knockingParticipant.email'>
                                        {smallScreen ? truncateText(p.email, 15) : p.email}
                                    </span>
                                )}
                            </div>
                            <button
                                className = 'primary'
                                data-testid = 'lobby.allow'
                                onClick = { this._onRespondToParticipant(p.id, true) }
                                type = 'button'>
                                {t('lobby.admit')}
                            </button>
                            <button
                                className = 'borderLess'
                                data-testid = 'lobby.reject'
                                onClick = { this._onRespondToParticipant(p.id, false) }
                                style = { rejectButtonStyle }
                                type = 'button'>
                                {t('lobby.reject')}
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    _onRespondToParticipant: (string, boolean) => Function;
}

/**
 * Maps part of the Redux state to the props of this component.
 *
 * @param {Object} state - The Redux state.
 * @returns {Props}
 */
function _mapStateToProps(state: Object): $Shape<Props> {
    return {
        ...abstractMapStateToProps(state),
        _toolboxVisible: isToolboxVisible(state),
        _isPictureInPictureVisible: getPictureInPictureModeConfig(state)
    };
}

export default translate(connect(_mapStateToProps)(KnockingParticipantList));
